import type { Landmark } from '../../../data/types';
import { readBool, readEnum, readNumber } from './params';

export type SpireKind = 'needle' | 'pyramid' | 'none';

/** Data parameters of a church or chapel; null means "derive from the footprint". */
export interface ChurchParams {
  readonly naveWidth: number | null;
  readonly naveHeight: number;
  readonly aisles: number;
  readonly aisleHeight: number;
  readonly apse: 'polygonal' | 'flat';
  readonly transept: boolean;
  readonly towerHeight: number;
  readonly towerWidth: number | null;
  readonly spire: SpireKind;
  readonly spireHeight: number | null;
  readonly ridgeTurret: boolean;
}

export const CHURCH_DEFAULTS = {
  churchHeight: 18,
  chapelHeight: 12,
  aisleHeightRatio: 0.6,
} as const;

function optionalNumber(
  params: Landmark['params'],
  key: string,
  warn: (msg: string) => void
): number | null {
  return params[key] === undefined ? null : readNumber(params, key, 0, warn);
}

export function parseChurchParams(landmark: Landmark, warn: (msg: string) => void): ChurchParams {
  const p = landmark.params;
  const chapel = landmark.kind !== 'church';
  const fallbackHeight = chapel ? CHURCH_DEFAULTS.chapelHeight : CHURCH_DEFAULTS.churchHeight;
  const naveHeight = readNumber(p, 'naveHeight', fallbackHeight, warn);
  const towerHeight = readNumber(p, 'towerHeight', 0, warn);
  return {
    naveWidth: optionalNumber(p, 'naveWidth', warn),
    naveHeight,
    aisles: readNumber(p, 'aisles', 0, warn),
    aisleHeight: readNumber(p, 'aisleHeight', naveHeight * CHURCH_DEFAULTS.aisleHeightRatio, warn),
    apse: readEnum(p, 'apse', ['polygonal', 'flat'] as const, 'polygonal', warn),
    transept: readBool(p, 'transept', false, warn),
    towerHeight,
    towerWidth: optionalNumber(p, 'towerWidth', warn),
    spire: readEnum(p, 'spire', ['needle', 'pyramid', 'none'] as const, 'needle', warn),
    spireHeight: optionalNumber(p, 'spireHeight', warn),
    ridgeTurret: readBool(p, 'ridgeTurret', towerHeight === 0, warn),
  };
}
